import { FC } from 'react'
import moment from 'moment'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import Separator from 'components/atoms/Separator/Separator'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { CreditCardPanePreset } from 'components/features/creditCard/creditCardPanes/CreditCardPanePreset'
import { cardUpcomingPaymentsSelector, currentCCLedgerSelector } from 'components/features/creditCard/creditCardStore/selectors'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { DATE_FORMAT_READABLE } from 'constants/datetime'
import { LabeledElement, LabeledElementList } from 'components/atoms/LabeledElement/LabeledElement'
import { PayCardAmountPaneButton, PayCardConfirmPaneButton } from 'components/features/pfm/Button/FloatingPaneButtons'
import UpcomingPaymentNotification from 'components/features/notifications/UpcomingPaymentNotification/UpcomingPaymentNotification'
import { find } from 'lodash'
import { AVAILABLE_CREDIT_HINT, BALANCE_HINT, MINIMUM_AMOUNT_DUE_HINT, STATEMENT_BALANCE_HINT } from 'components/features/creditCard/creditCardStore/constants'

const BalanceDetailsPane: FC<{}> = () => {

  const ledger = useStoreSelector(currentCCLedgerSelector)
  const upcomingPayments = useStoreSelector(cardUpcomingPaymentsSelector)
  const autoPayment = !!upcomingPayments ? find(upcomingPayments, ['schedule_type', 'autopay']) : undefined
  const autoPayButton = !!autoPayment ? (
    <PayCardConfirmPaneButton text='On' style='textOnly' size='mini' withChevron isAutoPay />
  ) : (
    <PayCardAmountPaneButton text='Set Up' style='textOnly' size='mini' isAutoPay />
  )

  if (!ledger) {
    return <LoadingOverlay />
  }

  const { statement_balance_remaining, current_balance, available_credit, payment_due_date, minimum_payment_remaining } = ledger

  return (
    <CreditCardPanePreset title='Balance Details'>
      <UpcomingPaymentNotification />
      <LabeledElement
        label='Current Balance'
        element={ formatUSCurrency(current_balance, 'penny') }
        theme='hero'
        vertical
      />
      <LabeledElementList>
        <LabeledElement
          label='Statement Balance'
          hint={ STATEMENT_BALANCE_HINT }
          element={ formatUSCurrency(statement_balance_remaining, 'penny') }
        />
        <LabeledElement
          label='Current Balance'
          hint={ BALANCE_HINT }
          element={ formatUSCurrency(current_balance, 'penny') }
        />
        <LabeledElement
          label='Available Credit'
          hint={ AVAILABLE_CREDIT_HINT }
          element={ formatUSCurrency(available_credit, 'penny') }
        />
        <Separator />
        <LabeledElement
          label='Payment Due Date'
          element={ moment(payment_due_date).format(DATE_FORMAT_READABLE) }
        />
        <LabeledElement
          label='Minimum Payment'
          hint={ MINIMUM_AMOUNT_DUE_HINT }
          element={ formatUSCurrency(minimum_payment_remaining, 'penny') }
        />
        <Separator />
        <LabeledElement
          label='AutoPay'
          element={ autoPayButton }
        />
      </LabeledElementList>
      <small>
        Balances do not include approved transactions that have not yet posted or
        payments that are pending or still processing.
      </small>

      <PayCardAmountPaneButton text='MAKE A PAYMENT' fullWidth />
    </CreditCardPanePreset>
  )
}

export default BalanceDetailsPane
